import { OrganisationProfile, AIRecommendation, RiskLevel } from '../types/database';
import { supabase } from '../lib/supabase';
import { createLogger } from '../lib/logger';
import { DatabaseError } from '../lib/errors';

const logger = createLogger('ProfileService');

export interface RiskAssessment {
  score: number;
  level: RiskLevel;
  recommendations: AIRecommendation[];
}

export function calculateRiskScore(profile: Partial<OrganisationProfile>): RiskAssessment {
  let score = 0;
  const recommendations: AIRecommendation[] = [];

  if (profile.processes_sensitive_data) {
    score += 2;
    if (!profile.security_measures?.includes('encryption')) {
      recommendations.push({
        priority: 1,
        action: 'Implement data encryption for sensitive personal data',
        reason: 'You process sensitive data (health/biometric/children\'s data) which requires enhanced security measures',
        pdpo_article: 'PDPO Article 27 - Security Requirements'
      });
    }
  }

  if (profile.employee_count === '51-200' || profile.employee_count === '201-500' || profile.employee_count === '500+') {
    score += 1;
  }

  if (profile.has_cross_border_transfers) {
    score += 2;
    if (!profile.has_data_processing_agreements) {
      recommendations.push({
        priority: 2,
        action: 'Establish data processing agreements with international recipients',
        reason: 'Cross-border data transfers require adequate safeguards and documentation',
        pdpo_article: 'PDPO Article 31 - Cross-Border Transfer Requirements'
      });
    }
  }

  const hasStrongSecurity = profile.security_measures?.includes('encryption') &&
                           profile.security_measures?.includes('access_controls') &&
                           profile.security_measures?.includes('backups');

  if (hasStrongSecurity) {
    score -= 2;
  } else {
    const missingSecurity: string[] = [];
    if (!profile.security_measures?.includes('encryption')) missingSecurity.push('encryption');
    if (!profile.security_measures?.includes('access_controls')) missingSecurity.push('access controls');
    if (!profile.security_measures?.includes('backups')) missingSecurity.push('regular backups');

    if (missingSecurity.length > 0) {
      recommendations.push({
        priority: 1,
        action: `Implement missing security measures: ${missingSecurity.join(', ')}`,
        reason: 'Strong security measures are essential to protect personal data from unauthorized access',
        pdpo_article: 'PDPO Article 27 - Security Requirements'
      });
    }
  }

  if (profile.previous_breaches_count && profile.previous_breaches_count > 0) {
    score += profile.previous_breaches_count;
    recommendations.push({
      priority: 1,
      action: 'Review and strengthen breach prevention measures',
      reason: `Your organization has experienced ${profile.previous_breaches_count} previous breach(es)`,
      pdpo_article: 'PDPO Article 28 - Data Breach Notification'
    });
  }

  if (!profile.has_dpo && (profile.processes_sensitive_data || profile.employee_count !== '1-10')) {
    score += 1;
    recommendations.push({
      priority: 2,
      action: 'Designate a Data Protection Officer (DPO)',
      reason: 'Organizations processing sensitive data or large volumes of personal data should have a designated DPO',
      pdpo_article: 'PDPO Article 29 - Data Protection Officer'
    });
  }

  if (!profile.has_privacy_policy) {
    score += 1;
    recommendations.push({
      priority: 1,
      action: 'Create and publish a privacy policy',
      reason: 'A privacy policy is required to inform data subjects about how their personal data is processed',
      pdpo_article: 'PDPO Article 12 - Privacy Notice Requirements'
    });
  }

  if (profile.shares_data_with_third_parties && !profile.has_data_processing_agreements) {
    recommendations.push({
      priority: 1,
      action: 'Establish data processing agreements with third parties',
      reason: 'When sharing personal data with third parties, formal agreements are required to ensure compliance',
      pdpo_article: 'PDPO Article 30 - Data Processor Requirements'
    });
  }

  if (!profile.consent_mechanisms || profile.consent_mechanisms.length === 0) {
    recommendations.push({
      priority: 2,
      action: 'Implement consent collection mechanisms',
      reason: 'You need proper methods to collect and document consent from data subjects',
      pdpo_article: 'PDPO Article 14 - Consent Requirements'
    });
  }

  if (profile.legal_bases?.includes('legitimate_interests') && !profile.has_privacy_policy) {
    recommendations.push({
      priority: 1,
      action: 'Document legitimate interests assessment',
      reason: 'When relying on legitimate interests as a legal basis, you must conduct and document a balancing test',
      pdpo_article: 'PDPO Article 13 - Lawful Processing'
    });
  }

  score = Math.max(1, Math.min(10, score));

  let level: RiskLevel;
  if (score <= 3) {
    level = 'low';
  } else if (score <= 6) {
    level = 'medium';
  } else {
    level = 'high';
  }

  recommendations.sort((a, b) => a.priority - b.priority);

  return {
    score,
    level,
    recommendations: recommendations.slice(0, 5)
  };
}

export async function saveProfile(organisationId: string, profileData: Partial<OrganisationProfile>): Promise<OrganisationProfile> {
  logger.debug('saveProfile called with organisationId:', organisationId);
  logger.debug('saveProfile profileData:', profileData);

  const riskAssessment = calculateRiskScore(profileData);
  logger.debug('Risk assessment calculated:', riskAssessment);

  const dataToSave = {
    ...profileData,
    organisation_id: organisationId,
    risk_score: riskAssessment.score,
    risk_level: riskAssessment.level,
    ai_recommendations: riskAssessment.recommendations,
    profile_completed_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  logger.debug('Data to save:', dataToSave);

  const { data: existingProfile, error: checkError } = await supabase
    .from('organisation_profile')
    .select('id')
    .eq('organisation_id', organisationId)
    .maybeSingle();

  if (checkError) {
    logger.error('Error checking for existing profile:', checkError);
    throw new DatabaseError('checking existing profile', checkError);
  }

  logger.debug('Existing profile check:', existingProfile);

  if (existingProfile) {
    logger.debug('Updating existing profile...');
    const { data, error } = await supabase
      .from('organisation_profile')
      .update(dataToSave)
      .eq('organisation_id', organisationId)
      .select()
      .single();

    if (error) {
      logger.error('Update error:', error);
      throw new DatabaseError('updating profile', error);
    }
    logger.debug('Profile updated successfully:', data);
    return data;
  } else {
    logger.debug('Inserting new profile...');
    const { data, error } = await supabase
      .from('organisation_profile')
      .insert(dataToSave)
      .select()
      .single();

    if (error) {
      logger.error('Insert error:', error);
      throw new DatabaseError('inserting profile', error);
    }
    logger.debug('Profile inserted successfully:', data);
    return data;
  }
}

export async function getProfile(organisationId: string): Promise<OrganisationProfile | null> {
  const { data, error } = await supabase
    .from('organisation_profile')
    .select('*')
    .eq('organisation_id', organisationId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function updateProfile(organisationId: string, profileData: Partial<OrganisationProfile>): Promise<OrganisationProfile> {
  const riskAssessment = calculateRiskScore(profileData);

  const dataToUpdate = {
    ...profileData,
    risk_score: riskAssessment.score,
    risk_level: riskAssessment.level,
    ai_recommendations: riskAssessment.recommendations,
    last_reviewed_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('organisation_profile')
    .update(dataToUpdate)
    .eq('organisation_id', organisationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}
